// Helper compartido de resolución del recorrido GPX de un servicio TP.
//
// Extrae la lógica que estaba inline en src/pages/Services/DetailService para
// reutilizarla desde el detalle de Services y desde la vista de Validación de
// Servicios (SV2-T10), sin cambiar el comportamiento:
//   (a) getTripsResume(code, 'gpx').url  -> verificado con HEAD
//   (b) fallback S3: <bucket>/tp_<code>.gpx  -> verificado con HEAD
//   (devuelve la URL string o null; los fallbacks de imagen/mensaje son de UI)
import { getTripsResume } from "./api_rombo_helper"

// Bucket S3 de los .gpx por stage. Reusa el flag de auth (VITE_APP_DEFAULTAUTH),
// igual que la implementación original de DetailService.
export const getGpxBaseUrl = () => {
  const env = import.meta.env.VITE_APP_DEFAULTAUTH
  return env === "dev"
    ? "https://s3.us-west-1.amazonaws.com/app-pt-trips.stg"
    : "https://s3.us-west-1.amazonaws.com/app-pt-trips.prod"
}

// Normaliza el code del servicio a la parte numérica: TP-123 -> "123".
// (lowercase -> guiones a guion_bajo -> quita el prefijo tp_)
export const normalizeTripCode = (code) =>
  String(code || "")
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/^tp_/, "")

// URL del .gpx en S3 para un code dado (re-antepone un único tp_).
export const buildS3GpxUrl = (code) =>
  `${getGpxBaseUrl()}/tp_${normalizeTripCode(code)}.gpx`

// Verifica que un recurso exista con un HEAD (no descarga el cuerpo).
const headOk = async (url) => {
  try {
    const resp = await fetch(url, { method: "HEAD" })
    return resp.ok
  } catch (_e) {
    return false
  }
}

// Resuelve la URL del GPX de un servicio a partir del trips resume.
// Devuelve string|null.
//
// NOTA (hotfix): el fallback a S3 (<bucket>/tp_<code>.gpx) quedó DESACTIVADO a
// propósito. Ese HEAD a S3 producía un 404 en la primera carga del detalle para
// servicios cuyo .gpx aún no existe en el bucket (p. ej. TP-166183), ensuciando
// la consola y escalonando la resolución del recorrido. El resume es la única
// fuente ahora; si no entrega una URL válida, no hay recorrido (null) y la UI
// cae a su estado vacío sin pegarle a S3.
export const resolveServiceGpxUrl = async (code) => {
  if (!code) return null
  try {
    const resume = await getTripsResume(code, "gpx")
    if (resume && resume.url && (await headOk(resume.url))) {
      return resume.url
    }
  } catch (_error) {
    // Resume no disponible (409/404/red): sin recorrido, no se intenta S3.
  }
  return null
}

// ---------------------------------------------------------------------------
// Decodificación de polyline5 (Google Encoded Polyline Algorithm, precisión 5)
// ---------------------------------------------------------------------------
//
// El contrato GRS (Onda 0) expone los trackings del servicio como cadenas
// polyline5 (`tracking_original`, `tracking_sanitized`, `tracking_from_planner`).
// Este helper vive junto a la resolución del recorrido GPX para centralizar
// aquí todo lo relacionado con "recorrido del servicio" y no duplicar el
// decodificador en varias vistas (lo consume el detalle de validación).
//
// Implementación del algoritmo estándar de Google (sin dependencia externa):
// deltas en zig-zag, acumulados, escalados por 1e-5. Devuelve [{lat,lng}].
export const decodePolyline5 = (encoded) => {
  if (!encoded || typeof encoded !== "string") return []
  const points = []
  let index = 0
  let lat = 0
  let lng = 0
  const len = encoded.length

  while (index < len) {
    let result = 0
    let shift = 0
    let byte
    // Latitud.
    do {
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20 && index < len)
    const dLat = result & 1 ? ~(result >> 1) : result >> 1
    lat += dLat

    result = 0
    shift = 0
    // Longitud.
    do {
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20 && index < len)
    const dLng = result & 1 ? ~(result >> 1) : result >> 1
    lng += dLng

    points.push({ lat: lat * 1e-5, lng: lng * 1e-5 })
  }
  return points
}
